import { addMinutes, differenceInMinutes } from "date-fns";

import { createUnit } from "frontend/api/application/request-unit";
import { Unit } from "frontend/interfaces/unit";
import { Modal } from "frontend/utils/modals/modal";
import { formatDate, DateFormat } from "shared/utils/date-utils";
import { createAppointment } from "frontend/api/application/request-appointments";

import CopyAppointmentComponent from "frontend/components/CopyAppointment.vue";

export interface CopyModalData {
  unit: Unit;
  newStartDate: Date;
  newEndDate: Date;
  notifyPatient: boolean;
  isLoading: boolean;
  remindPatient: boolean;
  originalPersonName: string;
  originalRoomName: string | null;
  newPersonId: string;
  newRoomId: string | null;
}
export class CopyModal extends Modal<
  typeof CopyAppointmentComponent,
  CopyModalData
> {
  private personIDOriginal: string | undefined;
  private roomIDOriginal: string | null = null;

  public setPersonIDOriginal(personIDOriginal: string): typeof this {
    this.personIDOriginal = personIDOriginal;
    return this;
  }

  public setRoomIDOriginal(roomIDOriginal: string | null): typeof this {
    this.roomIDOriginal = roomIDOriginal;
    return this;
  }

  protected modalComponent() {
    return CopyAppointmentComponent;
  }

  protected async onClose(
    userConfirmed: boolean,
    closeModal: () => void,
    _forceClose = true
  ): Promise<void> {
    super.onClose(userConfirmed, closeModal, false);

    const data = this.getData();
    if (!data) {
      closeModal();
      return;
    }

    if (userConfirmed) {
      try {
        data.isLoading = true;

        const appointment = data.unit.appointment;
        let success;

        if (appointment) {
          const timeDifference = differenceInMinutes(
            data.newStartDate,
            data.unit.from
          );

          success = await createAppointment({
            appointment: {
              patient_id: appointment.patient.id,
              appointment_type_id: appointment.appointment_type.id,
              order_time_in_minutes: appointment.order_time_in_minutes,
              do_not_remind: !data.remindPatient,
              do_not_notify: !data.notifyPatient,
            },
            units: [
              {
                from: formatDate(data.newStartDate, DateFormat.ISO),
                to: formatDate(data.newEndDate, DateFormat.ISO),
                color_id: data.unit.color_id,
                room_id: data.newRoomId,
                participation: {
                  person_id: data.newPersonId,
                },
                name: data.unit.name,
              },
              ...appointment.units.map((unit) => {
                const newFrom = addMinutes(unit.from, timeDifference);
                const newTo = addMinutes(unit.to, timeDifference);

                return {
                  from: formatDate(newFrom, DateFormat.ISO),
                  to: formatDate(newTo, DateFormat.ISO),
                  color_id: unit.color_id,
                  room_id: unit.room?.id ?? null,
                  participation: {
                    person_id: unit.participations[0].person_id,
                  },
                  name: unit.name,
                };
              }),
            ],
          });
        } else {
          success = await createUnit({
            unit: {
              from: formatDate(data.newStartDate, DateFormat.ISO),
              to: formatDate(data.newEndDate, DateFormat.ISO),
              color_id: data.unit.color_id,
              room_id: data.newRoomId,
              participations: {
                person_id: data.newPersonId,
              },
              name: data.unit.name,
            },
          });
        }
        //setup success logic in modals
        this.setSuccess(success);

        if (success) {
          closeModal();
        }
      } finally {
        data.isLoading = false;
      }
    } else {
      closeModal();
    }
  }
}
